import {CommonModule} from '@angular/common';
import {Component, Input} from '@angular/core';
import {FormControl, ReactiveFormsModule} from '@angular/forms';

import {MaterialModule} from '../../material.module';

@Component({
  selector: 'app-color-field',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MaterialModule],
  templateUrl: './color-field.component.html',
  styleUrls: ['./color-field.component.scss']
})
export class ColorFieldComponent {
  @Input({required: true}) control!: FormControl<string | null>;
  @Input({required: true}) label = '';
  @Input({required: true}) inputId = '';
  @Input() hint = '';
  @Input() placeholder = '#RRGGBB';
  @Input() supportsTransparent = false;
  @Input() defaultPreviewColor = '';
  @Input() disabled = false;

  get rawValue(): string {
    return String(this.control?.value ?? '');
  }

  get isDisabled(): boolean {
    return this.disabled || this.control?.disabled === true;
  }

  get trimmedValue(): string {
    return this.rawValue.trim();
  }

  get isDefault(): boolean {
    return this.trimmedValue.length === 0;
  }

  get isTransparent(): boolean {
    return this.trimmedValue.toLowerCase() === 'transparent';
  }

  get normalizedPreviewColor(): string | null {
    if (this.isTransparent) {
      return 'transparent';
    }

    const direct = this.normalizeHex(this.trimmedValue);
    if (direct) {
      return direct;
    }

    if (this.isDefault) {
      return this.normalizeHex(this.defaultPreviewColor.trim());
    }

    return null;
  }

  get displayValue(): string {
    if (this.isDefault) {
      return 'Default';
    }

    if (this.isTransparent) {
      return 'Transparent';
    }

    return this.trimmedValue;
  }

  get hasInvalidValue(): boolean {
    return !this.isDefault && !this.isTransparent && this.normalizeHex(this.trimmedValue) === null;
  }

  get previewTextColor(): string | null {
    const color = this.normalizedPreviewColor;
    if (!color || color === 'transparent') {
      return null;
    }

    const rgb = this.parseHex(color);
    if (!rgb) {
      return null;
    }

    const luminance = this.calculateLuminance(rgb);
    return luminance < 0.5 ? '#FFFFFF' : '#111827';
  }

  get pickerColor(): string {
    const previewColor = this.normalizedPreviewColor;
    if (previewColor && previewColor !== 'transparent') {
      return previewColor;
    }

    return this.normalizeHex(this.defaultPreviewColor.trim()) ?? '#FFFFFF';
  }

  applyPickerColor(value: string): void {
    if (this.isDisabled) {
      return;
    }

    const trimmed = `${value ?? ''}`.trim();
    if (this.supportsTransparent && trimmed.toLowerCase() === 'transparent') {
      this.commitValue('transparent');
      return;
    }

    const normalized = this.normalizeHex(trimmed);
    if (!normalized) {
      return;
    }

    this.commitValue(normalized);
  }

  openNativePicker(input: HTMLInputElement, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.isDisabled) {
      return;
    }

    if (typeof input.showPicker === 'function') {
      input.showPicker();
      return;
    }

    input.click();
  }

  setDefaultValue(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.isDisabled) {
      return;
    }

    this.commitValue('');
  }

  setTransparentValue(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.isDisabled || !this.supportsTransparent) {
      return;
    }

    this.commitValue('transparent');
  }

  private commitValue(value: string): void {
    this.control?.setValue(value);
    this.control?.markAsDirty();
    this.control?.markAsTouched();
    this.control?.updateValueAndValidity({emitEvent: true});
  }

  private normalizeHex(value: string): string | null {
    const match = value.match(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
    if (!match) {
      return null;
    }

    const hex = match[1];
    const expanded = hex.length === 3
      ? hex.split('').map((character) => character + character).join('')
      : hex;

    return `#${expanded.toUpperCase()}`;
  }

  private parseHex(value: string): {r: number; g: number; b: number} | null {
    const normalized = this.normalizeHex(value);
    if (!normalized) {
      return null;
    }

    const r = Number.parseInt(normalized.slice(1, 3), 16);
    const g = Number.parseInt(normalized.slice(3, 5), 16);
    const b = Number.parseInt(normalized.slice(5, 7), 16);

    if ([r, g, b].some((channel) => Number.isNaN(channel))) {
      return null;
    }

    return {r, g, b};
  }

  private calculateLuminance({r, g, b}: {r: number; g: number; b: number}): number {
    const toLinear = (channel: number): number => {
      const srgb = channel / 255;
      return srgb <= 0.03928 ? srgb / 12.92 : Math.pow((srgb + 0.055) / 1.055, 2.4);
    };

    return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
  }
}
